import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { getParts } from '../../services/partService';
import { getLocations } from '../../services/locationService';
import { adjustInventory, getItemByBarcode } from '../../services/inventoryService';
import { toast } from 'react-toastify';

const InventoryAdjust = () => {
  const navigate = useNavigate();

  const [parts, setParts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);

  const [adjustment, setAdjustment] = useState({
    part_id: '',
    location_id: '',
    transaction_type: 'receive',
    quantity_change: '',
    notes: ''
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch parts for dropdown
        const partsData = await getParts();
        setParts(partsData);

        // Fetch locations for dropdown
        const locationsData = await getLocations();
        setLocations(locationsData);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAdjustment({
      ...adjustment,
      [name]: value
    });
  };

  const handleBarcodeSearch = async () => {
    if (!barcodeInput) return;

    try {
      setBarcodeLoading(true);
      setError(null);

      const result = await getItemByBarcode(barcodeInput);

      if (result.type === 'part') {
        // If barcode is for a part
        setAdjustment({
          ...adjustment,
          part_id: result.part.id
        });
        toast.success(`Found part: ${result.part.internal_part_number}`);
      } else if (result.type === 'location') {
        // If barcode is for a location
        setAdjustment({
          ...adjustment,
          location_id: result.location.id
        });
        toast.success(`Found location: ${result.location.warehouse.name} - ${result.location.rack}-${result.location.shelf}`);
      }

      setBarcodeLoading(false);
    } catch (err) {
      console.error('Error searching barcode:', err);
      setError('Barcode not found. Please try again or select manually.');
      setBarcodeLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!adjustment.part_id || !adjustment.location_id || !adjustment.transaction_type || !adjustment.quantity_change) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Format quantity change based on transaction type
      let quantityChange = parseInt(adjustment.quantity_change);

      // Convert positive numbers to negative for transaction types that remove inventory
      if ((adjustment.transaction_type === 'issue') && quantityChange > 0) {
        quantityChange = -quantityChange;
      }

      // For 'adjust' and 'return' types, the user should enter the actual change value
      // (positive for adding, negative for removing)
      // For 'receive', positive values add inventory (no conversion needed)

      // Prepare data for API
      const adjustmentData = {
        part_id: adjustment.part_id,
        location_id: adjustment.location_id,
        transaction_type: adjustment.transaction_type,
        quantity_change: quantityChange,
        notes: adjustment.notes || undefined
      };

      // Call API to adjust inventory
      await adjustInventory(adjustmentData);

      toast.success('Inventory adjusted successfully');

      // Reset form or redirect
      if (e.nativeEvent.submitter.name === 'saveAndNew') {
        // Reset form for new entry
        setAdjustment({
          part_id: '',
          location_id: '',
          transaction_type: 'receive',
          quantity_change: '',
          notes: ''
        });
        setBarcodeInput('');
      } else {
        // Redirect to inventory list
        navigate('/inventory');
      }

      setSubmitting(false);
    } catch (err) {
      console.error('Error adjusting inventory:', err);
      setError(err.message || 'Failed to adjust inventory');
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1">
          Adjust Inventory
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Barcode Scanner Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Barcode Scanner
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Scan or Enter Barcode"
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  helperText="Scan a part or location barcode to quickly fill the form"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {barcodeLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <IconButton
                            onClick={handleBarcodeSearch}
                            disabled={!barcodeInput}
                            edge="end"
                          >
                            <SearchIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleBarcodeSearch}
                  disabled={barcodeLoading || !barcodeInput}
                  sx={{ height: 56 }}
                >
                  Search
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Part Selection
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Part</InputLabel>
                    <Select
                      name="part_id"
                      value={adjustment.part_id}
                      onChange={handleChange}
                      label="Part"
                      sx={{ minWidth: '200px' }}
                    >
                      <MenuItem value="">
                        <em>Select Part</em>
                      </MenuItem>
                      {parts.map(part => (
                        <MenuItem key={part.id} value={part.id}>
                          {part.internal_part_number} - {part.description}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Select the part to adjust inventory for</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Location</InputLabel>
                    <Select
                      name="location_id"
                      value={adjustment.location_id}
                      onChange={handleChange}
                      label="Location"
                      sx={{ minWidth: '200px' }}
                    >
                      <MenuItem value="">
                        <em>Select Location</em>
                      </MenuItem>
                      {locations.map(location => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.warehouse.name} - {location.rack}-{location.shelf}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Select the warehouse location</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Transaction Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Transaction Type</InputLabel>
                    <Select
                      name="transaction_type"
                      value={adjustment.transaction_type}
                      onChange={handleChange}
                      label="Transaction Type"
                    >
                      <MenuItem value="receive">Receive (Add)</MenuItem>
                      <MenuItem value="issue">Issue (Remove)</MenuItem>
                      <MenuItem value="adjust">Adjust (Correction)</MenuItem>
                      <MenuItem value="return">Return</MenuItem>
                      <MenuItem value="transfer">Transfer</MenuItem>
                    </Select>
                    <FormHelperText>Choose the type of inventory adjustment</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Quantity"
                    name="quantity_change"
                    value={adjustment.quantity_change}
                    onChange={handleChange}
                    inputProps={{
                      min: adjustment.transaction_type === 'adjust' ? undefined : "1"
                    }}
                    helperText={
                      adjustment.transaction_type === 'issue'
                        ? 'Enter positive number (will be subtracted from inventory)'
                        : adjustment.transaction_type === 'adjust'
                        ? 'Enter positive to add, negative to remove from inventory'
                        : 'Enter the quantity to add to inventory'
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes"
                    name="notes"
                    value={adjustment.notes}
                    onChange={handleChange}
                    helperText="Optional notes about this adjustment"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                component={RouterLink}
                to="/inventory"
                variant="outlined"
                color="secondary"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  name="saveAndNew"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={24} color="inherit" /> : <AddIcon />}
                >
                  {submitting ? 'Saving...' : 'Save & New'}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
                >
                  {submitting ? 'Saving...' : 'Save & Return'}
                </Button>
              </Box>
            </Box>
          </form>
        </>
      )}
    </Container>
  );
};

export default InventoryAdjust;
